/**
 * Multi-Location Allocation Dialog
 *
 * Allows users to allocate quantities from multiple warehouse locations
 * for a single product in an outbound order
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import { Progress } from '../../ui/progress';
import {
  X,
  Sparkles,
  Trash2,
  MapPin
} from 'lucide-react';
import inventoryService from '../../../services/inventoryService';

// Helper to check whether an inventory row matches a saved allocation
// (saved allocations only carry location + client + specification)
const matchesAllocation = (loc, alloc) => {
  const locClient = loc.source_client_id || loc.client_id || 'global';
  const allocClient = alloc.source_client_id || alloc.client_id || 'global';
  if ((loc.location || 'main') !== (alloc.location || 'main')) return false;
  if (locClient !== allocClient) return false;
  if (alloc.specification && loc.specification && alloc.specification !== loc.specification) return false;
  return true;
};

// Row keys use the row index: multiple rows can share the same
// location/client/specification, so field-based keys collide.
const getRowKey = (index) => `row_${index}`;

// Render a row's product code with the part that differs from the
// requested product code highlighted in red (common prefix/suffix stay
// muted, the differing middle segment is marked).
const ProductCodeWithDiff = ({ rowCode, targetCode }) => {
  if (!rowCode) {
    return <span className="text-sm text-muted-foreground">-</span>;
  }
  if (!targetCode || rowCode === targetCode) {
    return <span className="text-sm text-muted-foreground">{rowCode}</span>;
  }

  let start = 0;
  const maxPrefix = Math.min(rowCode.length, targetCode.length);
  while (start < maxPrefix && rowCode[start] === targetCode[start]) start++;

  let rowEnd = rowCode.length;
  let targetEnd = targetCode.length;
  while (rowEnd > start && targetEnd > start && rowCode[rowEnd - 1] === targetCode[targetEnd - 1]) {
    rowEnd--;
    targetEnd--;
  }

  return (
    <span className="text-sm font-mono">
      <span className="text-muted-foreground">{rowCode.slice(0, start)}</span>
      <span className="text-white font-bold bg-red-600 rounded px-1 shadow-sm">
        {rowCode.slice(start, rowEnd)}
      </span>
      <span className="text-muted-foreground">{rowCode.slice(rowEnd)}</span>
    </span>
  );
};

function MultiLocationAllocationDialog({ open, onClose, product, onConfirm }) {
  const [allocations, setAllocations] = useState({});
  const [error, setError] = useState(null);
  const [inventoryData, setInventoryData] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const initializedRef = useRef(false);

  // Fetch inventory data when dialog opens
  useEffect(() => {
    if (open && product) {
      const fetchInventory = async () => {
        setLoadingInventory(true);
        try {
          const productCode = product.product_code || product.product_id;
          const response = await inventoryService.getInventory(
            1, 1000, 'location', 'asc',
            { product_id: productCode },
            null // global view
          );
          setInventoryData(response.inventory || []);
        } catch (err) {
          console.error('Failed to fetch inventory:', err);
          setInventoryData([]);
        } finally {
          setLoadingInventory(false);
        }
      };
      fetchInventory();
    }
  }, [open, product]);

  // Reset state when dialog opens or product changes
  useEffect(() => {
    if (open && product) {
      setAllocations({});
      setError(null);
      initializedRef.current = false;
    }
  }, [open, product]);

  // Calculate totals
  const totals = useMemo(() => {
    if (!product) return { allocated: 0, required: 0, remaining: 0 };

    const allocated = Object.values(allocations).reduce((sum, qty) => sum + (qty || 0), 0);
    const required = product.quantity_planned || product.quantity_actual || 0;
    const remaining = required - allocated;

    return { allocated, required, remaining };
  }, [allocations, product]);

  // Filter and sort locations from inventory API
  const availableLocations = useMemo(() => {
    if (!inventoryData || inventoryData.length === 0) return [];

    const withStock = inventoryData.filter(loc =>
      (loc.available_quantity || 0) > 0
    );

    return withStock.sort((a, b) =>
      (b.available_quantity || 0) - (a.available_quantity || 0)
    );
  }, [inventoryData]);

  // Restore previously saved allocations once inventory rows are loaded.
  // Saved allocations carry only location/client/specification, so match them
  // back to row indices (first unused matching row wins).
  useEffect(() => {
    if (!open || !product || initializedRef.current) return;
    if (availableLocations.length === 0) return;

    if (product.locationAllocations && product.locationAllocations.length > 0) {
      const usedRows = new Set();
      const initialAllocations = {};
      product.locationAllocations.forEach(alloc => {
        if (!(alloc.quantity > 0)) return;
        const idx = availableLocations.findIndex(
          (loc, i) => !usedRows.has(i) && matchesAllocation(loc, alloc)
        );
        if (idx >= 0) {
          usedRows.add(idx);
          initialAllocations[getRowKey(idx)] = alloc.quantity;
        }
      });
      setAllocations(initialAllocations);
    }
    initializedRef.current = true;
  }, [open, product, availableLocations]);

  // Handle quantity change for a location (using composite key)
  const handleQuantityChange = (locationKey, locationData, value) => {
    const qty = parseFloat(value) || 0;

    if (!locationData) return;

    const availableQty = locationData.available_quantity || 0;

    if (qty > availableQty) {
      setError(`${locationData.location} 库存不足：仅有 ${availableQty}${product.unit || '支'}`);
      return;
    }

    setError(null);
    setAllocations(prev => ({
      ...prev,
      [locationKey]: qty
    }));
  };

  // Auto-allocate: Fill from locations with highest stock first
  const handleAutoAllocate = () => {
    const required = product.quantity_planned || product.quantity_actual || 0;
    const newAllocations = {};
    let remaining = required;

    availableLocations.forEach((loc, index) => {
      if (remaining <= 0) return;

      const availableQty = loc.available_quantity || 0;
      const allocate = Math.min(remaining, availableQty);
      if (allocate > 0) {
        newAllocations[getRowKey(index)] = allocate;
        remaining -= allocate;
      }
    });

    setAllocations(newAllocations);
    setError(null);
  };

  // Clear all allocations
  const handleClear = () => {
    setAllocations({});
    setError(null);
  };

  // Confirm allocation
  const handleConfirm = () => {
    // Validate total allocation
    if (totals.allocated === 0) {
      setError('请至少从一个位置分配数量');
      return;
    }

    if (totals.remaining > 0) {
      const frozenInfo = product.frozen_qty > 0 ? `\n\n注意：该产品已冻结 ${product.frozen_qty}${product.unit || '支'}，冻结部分无需手动分配。` : '';
      const confirmed = window.confirm(
        `还有 ${totals.remaining}${product.unit || '支'} 未分配。${frozenInfo}\n\n是否继续？`
      );
      if (!confirmed) return;
    }

    // Build allocation array from row-index keys
    const locationAllocations = Object.entries(allocations)
      .filter(([_, qty]) => qty > 0)
      .map(([rowKey, quantity]) => {
        const rowIndex = parseInt(rowKey.replace('row_', ''), 10);
        const locationData = availableLocations[rowIndex];
        return {
          location: locationData?.location || '',
          quantity,
          available_quantity: locationData?.available_quantity || 0,
          // Include source client info for cross-client transfer detection
          source_client_id: locationData?.source_client_id || locationData?.client_id,
          source_client_name: locationData?.client_name || locationData?.source_client_name,
          client_name: locationData?.client_name || locationData?.source_client_name,
          specification: locationData?.specification
        };
      });

    onConfirm({
      ...product,
      locationAllocations,
      totalAllocated: totals.allocated
    });
  };

  if (!product) return null;

  const progressPercentage = totals.required > 0 ? (totals.allocated / totals.required) * 100 : 0;
  const isOverAllocated = totals.allocated > totals.required;
  const isUnderAllocated = totals.allocated < totals.required;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex justify-between items-start">
            <div>
              <DialogTitle>多位置分配</DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {product.product_name} ({product.product_code})
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto pr-2">
          {/* Summary Card */}
        <Card className="p-4 mb-4 bg-muted">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-muted-foreground">
              需要数量
            </span>
            <Badge>
              {totals.required}{product.unit || '支'}
            </Badge>
          </div>

          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-muted-foreground">
              已分配
            </span>
            <Badge
              variant={isOverAllocated ? 'destructive' : totals.allocated === totals.required ? 'default' : 'secondary'}
              className={totals.allocated === totals.required ? 'bg-success/100' : ''}
            >
              {totals.allocated}{product.unit || '支'}
            </Badge>
          </div>

          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-muted-foreground">
              剩余
            </span>
            <span className="text-sm font-medium">
              {totals.remaining}{product.unit || '支'}
            </span>
          </div>

          <Progress
            value={Math.min(progressPercentage, 100)}
            className={`mt-2 h-2 ${
              isOverAllocated ? 'bg-destructive/20' : isUnderAllocated ? 'bg-warning/20' : 'bg-success/20'
            }`}
          />
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>×</Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Quick Actions */}
        <div className="flex gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleAutoAllocate}
          >
            <Sparkles className="h-4 w-4 mr-1" />
            智能分配
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClear}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            清空
          </Button>
        </div>

        {/* Location Allocation Table */}
        <Card className="border">
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">库位</th>
                  <th className="px-4 py-2 text-left font-semibold">名称</th>
                  <th className="px-4 py-2 text-left font-semibold">货号</th>
                  <th className="px-4 py-2 text-left font-semibold">规格</th>
                  <th className="px-4 py-2 text-left font-semibold">客户</th>
                  <th className="px-4 py-2 text-left font-semibold">可用库存</th>
                  <th className="px-4 py-2 text-left font-semibold w-[150px]">分配数量</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loadingInventory ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                      正在加载库存数据...
                    </td>
                  </tr>
                ) : availableLocations.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                      无可用库存
                    </td>
                  </tr>
                ) : (
                  availableLocations.map((loc, index) => {
      const availableQty = loc.available_quantity || 0;
                    const rowKey = getRowKey(index);
                    const targetCode = product.product_code || product.product_id || '';
                    const rowCode = loc.product_id || loc.product_code || '';
                    const codeMismatch = !!rowCode && !!targetCode && rowCode !== targetCode;
                    return (
                      <tr
                        key={rowKey}
                        className={codeMismatch
                          ? 'bg-red-100 hover:bg-red-200 shadow-[inset_4px_0_0_0_#dc2626]'
                          : 'hover:bg-muted'}
                      >
                        {/* Location Name */}
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">
                              {loc.location || '-'}
                            </span>
                          </div>
                        </td>

                        {/* Product Name */}
                        <td className="px-4 py-2">
                          <span className="text-sm">
                            {loc.name || loc.product_name || '-'}
                          </span>
                        </td>

                        {/* Product Code */}
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <ProductCodeWithDiff rowCode={rowCode} targetCode={targetCode} />
                            {codeMismatch && (
                              <Badge variant="destructive" className="text-xs font-bold animate-pulse">
                                货号不一致
                              </Badge>
                            )}
                          </div>
                        </td>

                        {/* Specification */}
                        <td className="px-4 py-2">
                          <span className="text-muted-foreground">
                            {loc.specification || '-'}
                          </span>
                        </td>

                        {/* Client */}
                        <td className="px-4 py-2">
                          <span className="text-sm">
                            {loc.client_name || loc.source_client_name || '-'}
                          </span>
                        </td>

                        {/* Available Quantity */}
                        <td className="px-4 py-2">
                          <Badge variant="outline" className="border-green-500 text-success">
                            {availableQty}{product.unit || '支'}
                          </Badge>
                        </td>

                        {/* Allocation Input */}
                        <td className="px-4 py-2">
                          <Input
                            type="number"
                            value={allocations[rowKey] || ''}
                            onChange={(e) => handleQuantityChange(rowKey, loc, e.target.value)}
                            placeholder="0"
                            min={0}
                            max={availableQty}
                            step={1}
                            className={`w-full ${
                              allocations[rowKey] > availableQty
                                ? 'border-red-500'
                                : ''
                            }`}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Info Messages */}
        {isOverAllocated && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>
              分配数量超出需要数量 {totals.allocated - totals.required}{product.unit || '支'}
            </AlertDescription>
          </Alert>
        )}

        {isUnderAllocated && totals.allocated > 0 && (
          <Alert variant="warning" className="mt-4 border-yellow-500 bg-warning/10">
            <AlertDescription>
              还有 {totals.remaining}{product.unit || '支'} 未分配
            </AlertDescription>
          </Alert>
        )}
        </div>

        <DialogFooter className="mt-4 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button
            onClick={handleConfirm}
            disabled={totals.allocated === 0 || isOverAllocated}
          >
            确认分配
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MultiLocationAllocationDialog;
